import { useSyncExternalStore } from 'react';

/**
 * Platform-wide light/dark theme. One source of truth: the `data-theme` attribute on <html>, which
 * index.css keys every color token off. Everything painted with tokens re-themes for free — that is
 * the whole reason the palette lives in CSS variables.
 *
 * Persistence: localStorage `cbx-theme`. A user who has never toggled follows the OS preference
 * (and keeps following it live until they express a choice — matching what the v3 studio does).
 */
export type Theme = 'light' | 'dark';

const KEY = 'cbx-theme';
const media = window.matchMedia('(prefers-color-scheme: dark)');
const listeners = new Set<() => void>();

function stored(): Theme | null {
  const v = localStorage.getItem(KEY);
  return v === 'light' || v === 'dark' ? v : null;
}

export function getTheme(): Theme {
  return stored() ?? (media.matches ? 'dark' : 'light');
}

function apply(theme: Theme, animate: boolean): void {
  const root = document.documentElement;
  if (animate) {
    root.classList.add('theme-anim');
    window.setTimeout(() => root.classList.remove('theme-anim'), 320);
  }
  if (theme === 'dark') root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
  for (const fn of listeners) fn();
}

export function setTheme(theme: Theme): void {
  localStorage.setItem(KEY, theme);
  apply(theme, true);
}

export function toggleTheme(): void {
  setTheme(getTheme() === 'dark' ? 'light' : 'dark');
}

/** Call once before render so the first paint is already in the right theme (no flash). */
export function initTheme(): void {
  apply(getTheme(), false);
  media.addEventListener('change', () => {
    if (!stored()) apply(getTheme(), true); // still following the OS
  });
}

export function useTheme(): Theme {
  return useSyncExternalStore(
    fn => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    getTheme,
  );
}
